import React, { FC } from 'react';
import { useUserContext } from '@blocs.auth';
import { captureHeapEvent, HEAP_EVENT_NAMES } from '@blocs.heap';
import { useTranslation } from '@blocs.i18n';
import { PromotionalBanner } from '@blocs.promotional-banner';
import { useRouter } from '@blocs.router';
import { appConfig } from '@minecraft.app-config';
import { gqlPlanTermEnum } from '@minecraft.graphql-types';
import routeHelper from '../../routes/routeHelper';

// Stand-in for the headshots banner photo. Replace when the final asset is ready.
const HEADSHOTS_PROMOTIONAL_BANNER_BG = `${appConfig.cdnRoot}/images/static/headshots-banner.png`;

const HEADSHOTS_PROMOTIONAL_BANNER_VIEWED_ARTISTS_KEY = 'headshotsPromotionalBannerViewedArtistIds';

const readViewedArtistIds = (): string[] => {
  try {
    const stored = window.localStorage.getItem(HEADSHOTS_PROMOTIONAL_BANNER_VIEWED_ARTISTS_KEY);
    if (!stored) {
      return [];
    }

    const parsed = JSON.parse(stored);

    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
};

export const rememberArtistViewedHeadshotsPage = (artistId: string | number) => {
  const artistKey = String(artistId);
  const viewedArtistIds = readViewedArtistIds();

  if (viewedArtistIds.includes(artistKey)) {
    return;
  }

  window.localStorage.setItem(
    HEADSHOTS_PROMOTIONAL_BANNER_VIEWED_ARTISTS_KEY,
    JSON.stringify([...viewedArtistIds, artistKey])
  );
};

export const hasArtistViewedHeadshotsPage = (artistId: string | number) =>
  readViewedArtistIds().includes(String(artistId));

export const HeadshotsPromotionalBanner: FC = () => {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const { subscription } = useUserContext();
  const isAnnualMembership = subscription?.plan?.term?.code === gqlPlanTermEnum.ANNUAL;

  return (
    <PromotionalBanner
      title={t('talent:headshots.promotionalBanner.title')}
      subtitle={t(
        isAnnualMembership
          ? 'talent:headshots.promotionalBanner.annualSubtitle'
          : 'talent:headshots.promotionalBanner.monthlySubtitle'
      )}
      ctaLabel={t('talent:headshots.promotionalBanner.cta')}
      onClick={() => {
        captureHeapEvent({ name: HEAP_EVENT_NAMES.TALENT_CLICK_GLOBAL_BANNER_BOOK_YOUR_HEADSHOTS });
        navigate({ to: routeHelper.headshots.link() });
      }}
      image={HEADSHOTS_PROMOTIONAL_BANNER_BG}
      testId="headshots-promotional-banner"
      ctaTestId="headshots-promotional-banner-cta"
      heapId="global-banner-book-your-headshots"
      mdProps={{
        backgroundImageConfig: {
          position: 'center 34%',
        },
      }}
    />
  );
};

export default HeadshotsPromotionalBanner;
